<template>
	<el-row>
		<el-col :span="18">
			<div class="base-form">				
				<h2>Сервисные операции</h2>		
				<el-button type="primary" :icon="circleIcon" size="large" @click="onAddOperationClick">
					Новая операция
				</el-button>
			</div>
		</el-col>
	</el-row>
	<el-row>
		<el-col :span="8">
			<div class="base-form">
				<el-input v-model="tableSearchQuery" style="max-width: 600px" clearable placeholder="Поиск по названию операции"
					class="input-with-select">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</div>
		</el-col>
	</el-row>
	<el-row>
		<el-col :span="24">
			<el-table
				row-key="id" 				
				v-loading="tableLoading"
				sortable="custom"
				:data="tableData"				
				:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"
				@selection-change="onTableSelectionChange"
				@sort-change="onTableSortingChange"
				@row-click="onTableRowClick">
				<el-table-column v-if="!isPickerModeEnabled" type="selection" />
				<el-table-column width="100" align="center" v-if="isPickerModeEnabled && pickerModeOnItemPickedHandler">
					<template #default="scope">
						<el-button
							size="small"
							type="primary"
							@click="() => pickerModeOnItemPickedHandler!(scope.row)">
							Выбрать
						</el-button>
					</template>
				</el-table-column>
				<el-table-column label="Операция" prop="name" sortable column-key="name" />
				<el-table-column label="Описание" prop="description" column-key="description" />				
				<el-table-column v-if="!isPickerModeEnabled" align="center">
					<template #default="scope">
						<el-button
							size="small"
							type="danger"
							@click="(e) => onDeleteOperationClick(scope.row.id, scope.row.name, e)">
							Удалить
						</el-button>
					</template>
				</el-table-column>
			</el-table>
		</el-col>
	</el-row>
	<el-row v-if="isPickerModeEnabled">
		<el-col>
			<div class="base-form">
				<el-button
					type="primary"
					:disabled="!tableSelectedRows?.length"
					@click="onPickJobOperationsClick">
						Выбрать ({{ tableSelectedRows.length }})
				</el-button>
				&nbsp;
				<el-button @click="onCloseCurrentTabClick">
					Отмена
				</el-button>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, Search } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElMessageBox, ElNotification } from 'element-plus';
import axiosHelper from '@/helpers/axiosHelper';
import ApplicationTab from '@/models/ApplicationTab';
import TabsDictionary from '@/variables/tabs';
import store from '@/store';
import EventHelper from '@/helpers/eventsHelper';
import { WatchStopHandle, watch } from 'vue';
import ServiceJobOperationModel from '@/models/ServiceJobOperationModel';
import PickerConfigurationModel from '@/models/PickerConfigurationModel';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink
	},
	props: {
		params: Array
	}
})
export default class ServiceJobOperationsView extends Vue {
	/** params */
	params!: Array<any>;

	/** constants */	
	circleIcon = CirclePlus;
	searchIcon = Search;
	isPickerModeEnabled = false;
	pickerModeOnItemPickedHandler: Function | null = null;
	
	/** view-models */
	tableLoading = false;
	tableData: Array<ServiceJobOperationModel> = [];
	tableSearchQuery = '';
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	tableSortColumn = 'name';
	tableSortOrder = 'ascending';
	tableSelectedRows: Array<ServiceJobOperationModel> = [];
	
	watchTableSearchQuery: WatchStopHandle | null = null;

	mounted() {
		this.initView();
		this.fetchJobOperationsAsync();

		this.watchTableSearchQuery = watch(
			() => this.tableSearchQuery,
			() => this.fetchJobOperationsAsync()
		);

		EventHelper.setEventListener('jobOperationsUpdate', () => this.fetchJobOperationsAsync());
	}

	unmounted() {
		if (this.watchTableSearchQuery) {
			this.watchTableSearchQuery();
		}

		EventHelper.removeEventListener('jobOperationsUpdate', () => this.fetchJobOperationsAsync());
	}

	initView() {
		const pickerModeArgument = this.params?.length ? this.params[0] as PickerConfigurationModel : null;
		/** check if picker mode activated */
		if (pickerModeArgument && 
			pickerModeArgument.isPickerModeEnabled === true &&
			pickerModeArgument.onItemPickedCallback) {
			this.isPickerModeEnabled = true;
			this.pickerModeOnItemPickedHandler = pickerModeArgument.onItemPickedCallback;
		}
	}

	onTableRowClick(row: ServiceJobOperationModel) {
		if (this.isPickerModeEnabled) {
			return;
		}		
		const viewOperationTab = TabsDictionary.jobOperationView;		
		viewOperationTab.params = [ row, false ];
		store.commit('addTab', viewOperationTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onAddOperationClick() {
		const addOperationTab = TabsDictionary.jobOperationAdd;
		const addNewOperation = true;
		addOperationTab.params = [ null, addNewOperation ];
		store.commit('addTab', addOperationTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	async fetchJobOperationsAsync() {		
		try {
			this.tableLoading = true;			
			const filterObject = {
				orderBy: this.tableSortColumn ? this.tableSortColumn : 'name',
				orderAsc: this.tableSortOrder == 'ascending' ? true : false,
				query: this.tableSearchQuery
			};
			const jwt = store.getters.jwt;
			const usersHttpClient = axiosHelper.createInstance(jwt);
			const response = await usersHttpClient.get('/api/serviceJobOperations', { params: filterObject });
			this.tableData = response.data;			
		} catch (e: any) {
			if (e.code == 'ERR_NETWORK') {
				ElNotification({
					message: `Ошибка соединения, не удалось загрузить список сервисных операций`,
					type: 'error',
				});
			} else {
				ElNotification({
					message: `Ошибка загрузка списка сервисных операций`,
					type: 'error',
				});
			}
			console.error('Error loading service operations list', e)
		} finally {
			this.tableLoading = false;
		}
	}

	async onDeleteOperationClick(operationId: number, operationName: string, e: Event) {
		e.stopPropagation();

		const relatedJobIdsToOperation = await this.getRelatedJobIdsAsync(operationId);

		if (relatedJobIdsToOperation === null) {
			ElNotification({
				message: 'Ошибка удаления операции сервисной работы',
				type: 'error',
			});
			return;
		}

		if (relatedJobIdsToOperation.length == 0) {
			ElMessageBox.confirm(
				'Сервисная операция будет удалена',
				{
					confirmButtonText: 'OK',
					cancelButtonText: 'Отмена',
					type: 'error',
				}
			)
			.then(() => this.deleteJobOperationAsync(operationId));		
		} else {
			ElMessageBox.alert(
				`Невозможно удалить операцию '${operationName}', на нее ссылаются сервисные работы (${relatedJobIdsToOperation.length})`,
				'Удаление'
			);
		}
	}

	async getRelatedJobIdsAsync(operationId: number): Promise<Array<number> | null> {
		try {
			const jwt = store.getters.jwt;
			const jobCategoriesHttpClient = axiosHelper.createInstance(jwt);
			const jobCategoriesResponse = await jobCategoriesHttpClient.get(`/api/servicejoboperations/rels/${operationId}`);
			return jobCategoriesResponse.data.map((_: number) => _);
		} catch (e) {			
			console.error(`Error checking service-job-operation #${operationId} for related jobs`, e);
			return null;
		}
	}

	async deleteJobOperationAsync(operationId: number) {
		try {
			this.tableLoading = true;
			const jwt = store.getters.jwt;
			const jobOperationsHttpClient = axiosHelper.createInstance(jwt);
			await jobOperationsHttpClient.delete(`/api/serviceJobOperations/${operationId}`);
			ElNotification({
				title: 'Сервисная операция удалена',
				type: 'success',
			});	
			EventHelper.dispatchEvent('jobOperationsUpdate');
		} catch (e) {			
			ElNotification({
				title: 'Ошибка, сервисная операция не была удалена',
				type: 'error',
			});	
			console.error(`Error deleting service-job-operation #${operationId}`, e)
		} finally {
			this.tableLoading = false;
		}		
	}

	onTableSortingChange(a: any) {
		this.tableSortColumn = a.prop ?? null;
		this.tableSortOrder == a.order;
		this.fetchJobOperationsAsync();
	}

	onTableSelectionChange(rows: Array<ServiceJobOperationModel>) {
		this.tableSelectedRows = rows.map(_ => new ServiceJobOperationModel(_));
	}

	/** handles callback and passes selected operations if picker-mode enabled */	
	onPickJobOperationsClick() {
		if (!this.pickerModeOnItemPickedHandler) {
			return;
		}
		this.pickerModeOnItemPickedHandler(this.tableSelectedRows);
	}

	onCloseCurrentTabClick() {		
		const currentTabId = store.getters.tab;
		store.commit('removeTab', currentTabId);
	}
}
</script>